<template>
  <div>
    <b-list-group :key="componentKey">
      <b-list-group-item v-for="(threshold, index) in thresholds" :key="index">
        <dx-range-selector :value.sync="threshold.value"
          :selectedRangeColor="threshold.color"
          @valueChanged="thresholdValueChanged(threshold, $event)">
          <dx-behavior />
          <dx-size :height="50" />
          <dx-scale :startValue="0" :endValue="100" :minorTickInterval="0.50" :minRange="0.50">
            <dx-scale-label :visible="false" />
          </dx-scale>
          <dx-slider-marker format="##0.00'%'" />
        </dx-range-selector>

        <b-form-group class="list-inline-item" :description="$t('Webportal.Minimum')">
          <dx-number-box :value.sync="threshold.value.startValue"
                        @valueChanged="thresholdStartValueChanged(threshold, $event)"
                        format="##0.00'%'" width="6em" stylingMode="underlined"
                        :show-spin-buttons="true" :step="0.5" :min="0" :max="threshold.value.endValue - 0.01"
                        :disabled="index == 0" />
        </b-form-group>
        <b-form-group class="list-inline-item" :description="$t('Webportal.Maximum')">
          <dx-number-box :value.sync="threshold.value.endValue"
                        @valueChanged="thresholdEndValueChanged(threshold, $event)"
                        format="##0.00'%'" width="6em" stylingMode="underlined"
                        :show-spin-buttons="true" :step="0.5" :min="threshold.value.startValue + 0.01" :max="100"
                        :disabled="index == thresholds.length - 1" />
        </b-form-group>
        <b-form-group class="list-inline-item" :description="$t('Webportal.Icon')">
          <b-form-radio-group buttons button-variant="outline-secondary" size="sm"
                              v-model="threshold.smiley"
                              @input="onInput(thresholds)">
            <b-form-radio value="frown-open"><font-awesome-icon :icon="['far', 'frown-open']" /></b-form-radio>
            <b-form-radio value="meh"><font-awesome-icon :icon="['far', 'meh']" /></b-form-radio>
            <b-form-radio value="grin"><font-awesome-icon :icon="['far', 'grin']" /></b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group class="list-inline-item" :description="$t('Webportal.Color')">
            <dx-color-box v-model="threshold.color" @valueChanged="onInput(thresholds)" width="10em" stylingMode="underlined" />
        </b-form-group>
        
        <b-button variant="outline-danger" size="sm" class="float-right align-bottom mt-4"
                  v-if="index > 0"
                  @click="removeThreshold(threshold)">
          <font-awesome-icon icon="trash-alt" /> {{ $t('Webportal.Delete') }}
        </b-button>
      </b-list-group-item>
    </b-list-group>

    <b-button class="mt-3" variant="secondary" size="sm"
              @click="addThreshold()">
      <font-awesome-icon icon="plus" /> {{ $t('Webportal.Administration.AddThreshold') }}
    </b-button>
    <b-button class="mt-3" variant="secondary" size="sm"
              @click="resetToDefaults()">
      <font-awesome-icon icon="sliders-h-square" /> {{ $t('Webportal.Administration.ResetToDefault') }}
    </b-button>
  </div>
</template>

<script>

import { DxRangeSelector, DxNumberBox, DxColorBox } from 'devextreme-vue';
import { DxBehavior, DxSize, DxScale, DxScaleLabel, DxSliderMarker, DxMinorTick } from 'devextreme-vue/range-selector';

export default {
  name: "ThresholdSelector",

  props: {
    value: {
      type: Array,
      default: null
    },
  },

  components : {
    DxRangeSelector,
    DxNumberBox,
    DxBehavior,
    DxSize,
    DxScale,
    DxScaleLabel,
    DxSliderMarker,
    DxColorBox
  },

  data() {
    return {
      thresholds: this.value,
      componentKey: 0,
    }
  },

  watch: {
    value (newVal, oldVal) {
      // Increment component key to force rerender of all thresholds (and not reuse components) when value changes
      this.componentKey += 1;
      this.thresholds = newVal;
    },
    thresholds (newVal, oldVal) {
      this.onInput(newVal);
    }
  },
  
  methods: {
    onInput(value) {
      this.$emit('input', value);
    },

    addThreshold(elementRef) {
      let threshold = { value: { startValue: 95, endValue: 100 }, smiley: "grin", color: "#000000"}

      // if no threshold exist, treat it like 'reset to defaults'
      if (!this.thresholds) {
        this.resetToDefaults();
      } else {
        this.thresholds.push(threshold);
      }
      this.thresholdValueChanged(threshold);
    },

    removeThreshold(threshold) {
      this.thresholds.splice(this.thresholds.findIndex(t => t == threshold), 1);
      this.thresholdValueChanged(this.thresholds[this.thresholds.length - 1]);
    },

    thresholdStartValueChanged(threshold, event) {
      threshold.value = { startValue: event.value, endValue: threshold.value.endValue };
      this.thresholdValueChanged(threshold);
    },

    thresholdEndValueChanged(threshold, event) {
      threshold.value = { startValue: threshold.value.startValue, endValue: event.value };
      this.thresholdValueChanged(threshold);
    },

    thresholdValueChanged(threshold, event) {
      this.checkThresholds(threshold);
      this.onInput(this.thresholds);
    },

    resetToDefaults() {
      this.thresholds = [
        { value: { startValue: 0, endValue: 70 - 0.01 }, smiley: "frown-open", color: "#b71402" },
        { value: { startValue: 70, endValue: 90 - 0.01 }, smiley: "meh", color: "#ffd900" },
        { value: { startValue: 90, endValue: 100 }, smiley: "grin", color: "#36c259" }
      ];
    },

    checkThresholds(mainThreshold) {
      let indexMain = this.thresholds.findIndex(t => t == mainThreshold);

      // Check lower bound of 0 for first threshold
      if (indexMain == 0 && mainThreshold.value.startValue > 0)
        mainThreshold.value = { startValue: 0, endValue: mainThreshold.value.endValue };

      // Check upper bound of 100 for last threshold
      if (indexMain == this.thresholds.length - 1 && mainThreshold.value.endValue < 100)
        mainThreshold.value = { startValue: mainThreshold.value.startValue, endValue: 100 };

      // Adapt boundaries of all following thresholds
      let lastMaxValue = Math.min(mainThreshold.value.endValue + 0.01, 100);
      for (let i = indexMain + 1; i < this.thresholds.length; i++)
      {
        let t = this.thresholds[i];
        if (t.value.startValue != lastMaxValue)
          t.value = { startValue: lastMaxValue, endValue: Math.max(t.value.endValue, Math.min(lastMaxValue, 100)) };
        lastMaxValue = Math.min(t.value.endValue + 0.01, 100);
      }

      // Adapt boundaries of all preceding thresholds
      let lastMinValue = Math.max(mainThreshold.value.startValue - 0.01, 0);
      for (let i2 = indexMain - 1; i2 >= 0; i2--)
      {
        let t2 = this.thresholds[i2];
        if (t2.value.endValue != lastMinValue)
          t2.value = { startValue: Math.min(t2.value.startValue, Math.max(lastMinValue, 0)), endValue: lastMinValue };
        lastMinValue = Math.max(t2.value.startValue - 0.01, 0);
      }
    }
  }
}
</script>